/**
 * Field diagnostics for real phones: open the game with ?diag to see a small overlay with the
 * device, the fight start-up steps and any errors, so a screenshot tells us where it got stuck.
 * It also meters frames over the last 10 s: frame rate, hitches (a frame late by more than a
 * quarter of a 60 Hz frame) and the fight's own main-thread time per frame. Low fps with a small
 * fight time means the GPU or the browser is the limit, not the game code.
 */
declare const __BUILD_TIME__: string;

interface Mark {
  t: number;
  name: string;
}

const enabled = typeof location !== 'undefined' && new URLSearchParams(location.search).has('diag');
const marks: Mark[] = [];
const errors: string[] = [];
const t0 = performance.now();

const METER_WINDOW_MS = 10_000;
const HITCH_MS = 1000 / 60 + 4;
/** Recent frame timestamps (rAF time, ms). */
const frames: number[] = [];
/** Recent fight frame costs: [time, ms]. */
const work: [number, number][] = [];

export const diag = {
  enabled,
  mark(name: string): void {
    marks.push({ t: Math.round(performance.now() - t0), name });
    if (marks.length > 40) marks.shift();
  },
  error(msg: string): void {
    errors.push(msg.slice(0, 160));
    if (errors.length > 8) errors.shift();
  },
  /** Main-thread time of one fight frame (simulation, bot, scene, HUD). Call only when enabled. */
  frameWork(ms: number): void {
    work.push([performance.now(), ms]);
  },
};

function meterLine(): string | null {
  const now = performance.now();
  while (frames.length && frames[0]! < now - METER_WINDOW_MS) frames.shift();
  while (work.length && work[0]![0] < now - METER_WINDOW_MS) work.shift();
  if (frames.length < 2) return null;
  let hitches = 0;
  let worst = 0;
  for (let i = 1; i < frames.length; i++) {
    const gap = frames[i]! - frames[i - 1]!;
    if (gap > HITCH_MS) hitches++;
    worst = Math.max(worst, gap);
  }
  const fps = ((frames.length - 1) * 1000) / (frames[frames.length - 1]! - frames[0]!);
  let line = `frames ${fps.toFixed(0)} fps · ${hitches} hitches/10 s · worst ${worst.toFixed(0)} ms`;
  if (work.length) {
    const ms = work.map((w) => w[1]).sort((a, b) => a - b);
    const avg = ms.reduce((a, b) => a + b, 0) / ms.length;
    const p95 = ms[Math.floor(0.95 * (ms.length - 1))]!;
    line += `\nfight js ${avg.toFixed(1)} ms avg · ${p95.toFixed(1)} ms p95`;
  }
  return line;
}

if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => diag.error(`error: ${e.message}`));
  window.addEventListener('unhandledrejection', (e) => diag.error(`rejection: ${String(e.reason)}`));
}

if (enabled) {
  const el = document.createElement('pre');
  el.style.cssText =
    'position:fixed;left:calc(4px + env(safe-area-inset-left));top:calc(4px + env(safe-area-inset-top));z-index:2147483647;max-width:70vw;max-height:55vh;overflow:auto;margin:0;padding:6px 8px;' +
    'font:10px/1.35 ui-monospace,Menlo,monospace;color:#f2ede4;background:rgba(0,0,0,.72);border-radius:8px;pointer-events:none;white-space:pre-wrap';
  const render = () => {
    const o = screen.orientation?.type ?? '?';
    const meter = meterLine();
    el.textContent = [
      `build ${__BUILD_TIME__}`,
      navigator.userAgent.replace(/\(KHTML, like Gecko\) /, ''),
      `viewport ${innerWidth}×${innerHeight} dpr ${devicePixelRatio} ${o} hidden=${document.hidden}`,
      ...(meter ? [meter] : []),
      ...marks.map((m) => `${String(m.t).padStart(6)}ms ${m.name}`),
      ...errors.map((e) => `!! ${e}`),
    ].join('\n');
  };
  const mount = () => document.body.appendChild(el);
  if (document.body) mount();
  else addEventListener('DOMContentLoaded', mount);
  setInterval(render, 500);
  const tick = (t: number) => {
    frames.push(t);
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}
